import { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import axios from 'axios';
import { X, Loader2, CheckCircle2 } from 'lucide-react';
import { useUserAuth } from '../contexts/UserAuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// iter674 · "Did FLYT send you a pre-made FLYT ID?" — the post-login claim
// sheet for Google/Apple users (and anyone) to connect their pre-built
// profile with the single-use code Jay DM'd them. Liquid-glass, dark.
export default function ClaimCodeSheet() {
  const { claimSheetOpen, setClaimSheetOpen, refreshUser } = useUserAuth();
  const [code, setCode] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [done, setDone] = useState(false);

  // Prefill a code stashed in localStorage by an older flow, if present.
  useEffect(() => {
    if (!claimSheetOpen) return;
    try {
      const pending = (localStorage.getItem('flyt_pending_claim_code') || '').toUpperCase();
      if (pending) setCode(pending);
    } catch { /* ignore */ }
  }, [claimSheetOpen]);

  if (!claimSheetOpen) return null;

  const close = () => { setClaimSheetOpen(false); setCode(''); setError(''); setDone(false); };

  const submit = async () => {
    const c = code.trim().toUpperCase();
    if (!c) { setError('Enter the code FLYT sent you.'); return; }
    setBusy(true); setError('');
    try {
      await axios.post(`${API}/me/claim-code`, { code: c });
      setDone(true);
      try { localStorage.removeItem('flyt_pending_claim_code'); } catch { /* ignore */ }
      try { await refreshUser?.(); } catch { /* ignore */ }
      setTimeout(() => { close(); }, 1600);
    } catch (err) {
      const raw = err?.response?.data?.detail;
      setError((typeof raw === 'string' ? raw : raw?.message) || "That code didn't work. Double-check and try again.");
    } finally { setBusy(false); }
  };

  return createPortal(
    <div className="fixed inset-0 z-[130] flex items-center justify-center bg-black/70 backdrop-blur-md p-4" onClick={close} data-testid="claim-code-sheet">
      <div
        className="relative w-full max-w-sm rounded-3xl border border-white/10 bg-white/[0.06] backdrop-blur-2xl p-6 text-white shadow-2xl"
        style={{ background: 'linear-gradient(160deg, rgba(255,255,255,0.10), rgba(255,255,255,0.03))' }}
        onClick={(e) => e.stopPropagation()}
      >
        <button onClick={close} className="absolute top-4 right-4 text-white/50 hover:text-white transition" aria-label="Close" data-testid="claim-code-close">
          <X className="w-5 h-5" />
        </button>

        {done ? (
          <div className="py-6 text-center" data-testid="claim-code-success">
            <CheckCircle2 className="w-12 h-12 text-green-400 mx-auto mb-3" />
            <p className="font-display text-2xl uppercase leading-tight">You're live.</p>
            <p className="text-sm text-white/60 mt-1">Your FLYT ID is connected. Welcome in.</p>
          </div>
        ) : (
          <>
            <p className="font-italic text-xs uppercase tracking-widest text-blue-300 mb-2">Claim your profile</p>
            <h2 className="font-display text-2xl uppercase leading-[1.05] mb-1">Did FLYT send you a code?</h2>
            <p className="text-sm text-white/60 mb-5">If we pre-built your FLYT ID, drop the code from your message below to connect it. No code? Just close this.</p>

            <input
              type="text"
              value={code}
              onChange={(e) => setCode(e.target.value.toUpperCase())}
              placeholder="FLYT-XXXXX"
              autoFocus
              className="w-full text-center tracking-[0.25em] text-lg font-bold text-white bg-white/5 ring-1 ring-white/15 focus:ring-blue-400 rounded-xl py-3 outline-none placeholder-white/25 uppercase"
              data-testid="claim-code-input"
            />
            {error && <p className="text-xs text-red-300 bg-red-500/10 border border-red-500/30 rounded-lg px-3 py-2 mt-3" data-testid="claim-code-error">{error}</p>}

            <button
              onClick={submit}
              disabled={busy || !code.trim()}
              className="w-full mt-4 inline-flex items-center justify-center gap-2 px-5 py-3 rounded-full bg-white text-black font-bold text-sm hover:bg-gray-100 transition disabled:opacity-50"
              data-testid="claim-code-submit"
            >
              {busy && <Loader2 className="w-4 h-4 animate-spin" />}
              {busy ? 'Connecting…' : 'Claim now'}
            </button>
            <button onClick={close} className="w-full mt-2 text-xs text-white/50 hover:text-white transition" data-testid="claim-code-dismiss">
              I don't have a code
            </button>
          </>
        )}
      </div>
    </div>,
    document.body
  );
}
